import {isPlausibleGradYear} from '../components/grad-year/grad-year-util';

/**
 * The redesign's size questions ("Degree you're framing", "When did you graduate?"), built from the current school's diploma size
 * rows (dbo.DocSize via s.docsizes_available). Pure, so the rules are unit-tested on real licensor shapes (size-questions.test.ts):
 *
 * - The degree buttons are the data's size groups (DocSizeName: "Bachelors/Masters", "Doctorate/PhD"...), asked only when two groups
 *   give a different size for some year.
 * - A group asks for the year when its date windows give two or more sizes. A window runs from its StartDate to the end of its
 *   EndDate's month (EndDate is the last month a size applies; 2100 means current, 1775/1776/1860 any older document).
 * - A year picks the window that contains July 1 of that year (diplomas are mostly issued in May or June); a year outside every
 *   window takes the nearest window.
 */

/** The DocSize fields the questions read (ProductDocSize carries more). */
export type DocSizeRow = {
    Id: number;
    DocSizeName: string;
    Doc_Width: number;
    Doc_Height: number;
    StartDate: string | Date;
    EndDate: string | Date;
};

export type SizeWindow = {row: DocSizeRow; start: number; end: number};

export type DegreeGroup = {
    /** The data's DocSizeName, trimmed: the group's identity. */
    key: string;
    /** The button label: "Bachelor's or Master's". */
    label: string;
    windows: SizeWindow[];
    /** The group's windows give two or more sizes. */
    needsYear: boolean;
};

export type SizeQuestions = {
    groups: DegreeGroup[];
    /** Two or more groups give a different size for some year. */
    degreeNeeded: boolean;
};

export type SizeAnswers = {
    /** The answered degree group's key, or null. */
    degree: string | null;
    /** The year field's text (digits). */
    year: string;
};

const DAY = 24 * 60 * 60 * 1000;

/** UTC milliseconds of a DocSize date's calendar day ("2024-01-01T00:00:00", a Date, or anything Date parses). */
export function parseDocSizeDate(value: string | Date): number {
    const text = typeof value === 'string' ? value : value instanceof Date ? value.toISOString() : String(value);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (m) {
        return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
    const parsed = new Date(text);
    return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** A row's window: StartDate through the last moment of EndDate's month. */
export function windowOf(row: DocSizeRow): SizeWindow {
    const start = parseDocSizeDate(row.StartDate);
    const endMonth = new Date(parseDocSizeDate(row.EndDate));
    const end = Date.UTC(endMonth.getUTCFullYear(), endMonth.getUTCMonth() + 1, 1) - 1;
    return {row, start, end};
}

/** July 1 of a year, the moment the year rule looks up. */
export function july1(year: number): number {
    return Date.UTC(year, 6, 1);
}

/** The window that contains the moment (the most recent start when windows overlap), else the nearest one. */
export function windowAt(windows: SizeWindow[], t: number): SizeWindow {
    const containing = windows.filter(w => w.start <= t && t <= w.end);
    if (containing.length > 0) {
        return containing.reduce((a, b) => (b.start > a.start ? b : a));
    }
    const distance = (w: SizeWindow) => (t < w.start ? w.start - t : t - w.end);
    return windows.reduce((a, b) => (distance(b) < distance(a) ? b : a));
}

export const sizeKey = (row: Pick<DocSizeRow, 'Doc_Width' | 'Doc_Height'>): string => `${Number(row.Doc_Width)}x${Number(row.Doc_Height)}`;

const DEGREE_WORDS: [RegExp, string][] = [
    [/^associates?$/i, "Associate's"],
    [/^bachelors?$/i, "Bachelor's"],
    [/^masters?$/i, "Master's"],
    [/^ph\.?d\.?s?$/i, 'PhD'],
    [/^doctorates?$/i, 'Doctorate'],
    [/^jd$/i, 'JD'],
    [/^postgraduate$/i, 'Postgraduate'],
    [/^postdoctoral$/i, 'Postdoctoral'],
    [/^(hs|high school)$/i, 'High School'],
    [/^prep school$/i, 'Prep School'],
];

/** The degree words of a group name ("Bachelors/Masters" → Bachelor's, Master's), or null when a part is not a known degree. */
export function degreeParts(name: string): string[] | null {
    const parts = (name || '')
        .split('/')
        .map(p => p.trim())
        .filter(p => p !== '');
    if (parts.length === 0) return null;
    const words = parts.map(p => DEGREE_WORDS.find(([re]) => re.test(p))?.[1]);
    return words.every((w): w is string => typeof w === 'string') ? words : null;
}

const joinWords = (words: string[], conjunction: 'or' | 'and'): string => {
    if (words.length <= 1) return words.join('');
    if (words.length === 2) return `${words[0]} ${conjunction} ${words[1]}`;
    return `${words.slice(0, -1).join(', ')} ${conjunction} ${words[words.length - 1]}`;
};

/** A degree button: "Bachelors/Masters" → "Bachelor's or Master's". Names that are not degree lists show as written. */
export function degreeLabel(name: string): string {
    const words = degreeParts(name);
    return words ? joinWords(words, 'or') : (name || '').trim();
}

/** The degrees a size fits, for "Made to fit": "Bachelors/Masters/PhD" → "Bachelor's, Master's and PhD" (PlaceHolderService wording, no article). */
export function degreeWords(name: string): string {
    const words = degreeParts(name);
    return words ? joinWords(words, 'and') : (name || '').trim();
}

/** The years a window covers, for "Size set": "2024 or later", "before 2024", "2005 to 2023". */
export function windowYears(window: SizeWindow): string {
    const startYear = new Date(window.start).getUTCFullYear();
    const endYear = new Date(window.end).getUTCFullYear();
    if (startYear <= 1860 && endYear >= 2100) return 'any year';
    if (startYear <= 1860) return `before ${endYear + 1}`;
    if (endYear >= 2100) return `${startYear} or later`;
    return startYear === endYear ? `${startYear}` : `${startYear} to ${endYear}`;
}

/** The size question groups for the current school's rows, in the rows' order (s.docsizes_available lists Bachelor's first). */
export function buildSizeQuestions(rows: DocSizeRow[]): SizeQuestions {
    const byKey = new Map<string, SizeWindow[]>();
    for (const row of rows ?? []) {
        const key = (row.DocSizeName || '').trim();
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key)!.push(windowOf(row));
    }
    const groups: DegreeGroup[] = [...byKey.entries()].map(([key, windows]) => ({
        key,
        label: degreeLabel(key),
        windows,
        needsYear: new Set(windows.map(w => sizeKey(w.row))).size > 1,
    }));
    return {groups, degreeNeeded: groupsDiffer(groups)};
}

/** Two groups give a different size at some moment: checked at every window edge and between them (a gap switches at its middle). */
function groupsDiffer(groups: DegreeGroup[]): boolean {
    if (groups.length < 2) return false;
    const edges = [...new Set(groups.flatMap(g => g.windows.flatMap(w => [w.start, w.end + 1])))].sort((a, b) => a - b);
    const moments = [edges[0] - DAY, ...edges];
    for (let i = 0; i < edges.length - 1; i++) {
        moments.push(Math.floor((edges[i] + edges[i + 1]) / 2));
    }
    return moments.some(t => new Set(groups.map(g => sizeKey(windowAt(g.windows, t).row))).size > 1);
}

/** A year the size question accepts: four digits, the graduation year's own plausibility rule (1000 to 3000). */
export function isAnsweredYear(text: string): boolean {
    return /^\d{4}$/.test(text || '') && isPlausibleGradYear(text);
}

export function groupByKey(q: SizeQuestions, key: string | null): DegreeGroup | undefined {
    return key == null ? undefined : q.groups.find(g => g.key === key);
}

/** The group a row belongs to. */
export function groupOfRow(q: SizeQuestions, rowId: number): DegreeGroup | undefined {
    return q.groups.find(g => g.windows.some(w => w.row.Id === rowId));
}

/** The year question shows: for the answered degree, when its sizes change with the year; before that, when any group's do. */
export function yearShown(q: SizeQuestions, degree: string | null): boolean {
    const group = groupByKey(q, degree);
    return group ? group.needsYear : q.groups.some(g => g.needsYear);
}

/** The questions still open, in step order. */
export function openQuestions(q: SizeQuestions, answers: SizeAnswers): ('degree' | 'year')[] {
    const open: ('degree' | 'year')[] = [];
    if (q.degreeNeeded && !groupByKey(q, answers.degree)) open.push('degree');
    if (yearShown(q, answers.degree) && !isAnsweredYear(answers.year)) open.push('year');
    return open;
}

/**
 * The size the answers set, or null while the degree they need is open. A degree answered before the year takes its window for
 * today (the price follows the degree at once); a year applies the July 1 rule. With no degree question the current row's group
 * answers it (all groups give the same size).
 */
export function rowForAnswers(q: SizeQuestions, answers: SizeAnswers, currentRowId: number, now: number): DocSizeRow | null {
    if (q.groups.length === 0) return null;
    let group = groupByKey(q, answers.degree);
    if (!group) {
        if (q.degreeNeeded) return null;
        group = groupOfRow(q, currentRowId) ?? q.groups[0];
    }
    const t = isAnsweredYear(answers.year) ? july1(Number(answers.year)) : now;
    return windowAt(group.windows, t).row;
}
